import React, { useState, useEffect } from "react";
import UserScreenWindow from "../Components/UserScreenWindow";
import { useSelector } from "react-redux";
import Select from "react-select";
import { v4 } from "uuid";
import { storage } from "../../../utils/firebase/Firebase.utils";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import {
  setDocData,
  queryDocByFieldSnap,
} from "../../../utils/firebase/Firebase.utils";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FaTools,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaPaperPlane,
  FaClock,
  FaWrench,
  FaExclamationCircle,
  FaTimes,
  FaCheck,
  FaArrowRight,
  FaHardHat,
  FaPlus,
  FaSearch,
  FaFilter,
  FaSortAmountDown,
  FaSortAmountUp,
  FaChevronLeft,
  FaUsers,
  FaComments,
  FaUser,
  FaMoneyBillWave,
  FaCheckCircle,
} from "react-icons/fa";
import FileUploadAndView from "../Components/FileUploadAndView";
import PhotoCarousel from "../Marketplace/PhotoCarousel";
import { unitNumberLabel } from "../../../utils/constants";

function ServiceRequest() {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.userObject);
  const [serviceType, setServiceType] = useState("");
  const [otherServiceType, setOtherServiceType] = useState("");
  const [formData, setFormData] = useState({
    identifier: currentUser.linkedIdentifiers[0],
    description: "",
    preferredDate: "",
    preferredTime: "",
  });
  const [progress, setProgress] = useState(0);
  const [activeUpload, setActiveUpload] = useState("");
  const [files, setFiles] = useState({});
  const [requestId, setRequestId] = useState(v4().slice(0, 8).toUpperCase());
  const [isProcessing, setIsProcessing] = useState(false);
  const [userRequests, setUserRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");

  useEffect(() => {
    const unsubscribe = queryDocByFieldSnap(
      "serviceRequests",
      "userId",
      "==",
      currentUser.uid,
      (requests) => {
        setUserRequests(requests);
        setIsLoading(false);
      }
    );

    return () => unsubscribe && unsubscribe();
  }, [currentUser.uid]);

  useEffect(() => {
    // Apply search filter and sorting
    let filtered = [...userRequests];

    // Apply status filter
    if (activeFilter !== "All") {
      filtered = filtered.filter((request) => request.status === activeFilter);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const term = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((request) => {
        // Search in service type
        if (request.serviceType?.toLowerCase().includes(term)) return true;
        // Search in description
        if (request.description?.toLowerCase().includes(term)) return true;
        // Search in unit identifier
        if (request.identifier?.toLowerCase().includes(term)) return true;
        return false;
      });
    }

    // Apply sort order
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

    setFilteredRequests(filtered);
  }, [searchQuery, userRequests, sortOrder, activeFilter]);

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "newest" ? "oldest" : "newest");
  };

  const serviceTypes = [
    "Plumbing",
    "Carpentry",
    "Painting",
    "Electrical",
    "Air Conditioning",
    "Others",
  ];

  const identifiers = currentUser.linkedIdentifiers.map((identifier) => ({
    value: identifier,
    label: identifier,
  }));

  const options = serviceTypes.map((type) => ({
    value: type,
    label: type,
  }));

  const timeSlots = [
    "8:00 AM - 10:00 AM",
    "10:00 AM - 12:00 PM",
    "1:00 PM - 3:00 PM",
    "3:00 PM - 5:00 PM",
  ].map((time) => ({
    value: time,
    label: time,
  }));

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "35px",
      border: "1px solid #e5e7eb",
      boxShadow: "none",
      borderRadius: "6px",
      "&:hover": {
        border: "1px solid #363062",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
    }),
    input: (base) => ({
      ...base,
      margin: "0px",
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "0.75rem",
      padding: "8px",
      backgroundColor: state.isSelected
        ? "#363062"
        : state.isFocused
        ? "#f3f4f6"
        : null,
      color: state.isSelected ? "white" : "#374151",
    }),
  };

  const handleSelectServiceType = (selectedService) => {
    setServiceType(selectedService.value);
  };

  const handleSelectUnit = (selected) => {
    setFormData({ ...formData, identifier: selected.value });
  };

  const handleSelectTime = (selected) => {
    setFormData({ ...formData, preferredTime: selected.value });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleUpload = (e, fileName) => {
    const file = e.target.files[0];
    const storageRef = ref(
      storage,
      `serviceRequests/${requestId}/${file.name}_${v4()}`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);
    setActiveUpload(fileName);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(progress);
      },
      (error) => {
        console.error("Upload error:", error);
        toast.error("Failed to upload file");
        setActiveUpload("");
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          setFiles({
            ...files,
            [fileName]: downloadURL,
          });
          setFormData({ ...formData, [fileName]: downloadURL });
          setActiveUpload("");
        });
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    // Validation
    if (!serviceType) {
      toast.error("Please select a service type");
      setIsProcessing(false);
      return;
    }

    if (serviceType === "Others" && !otherServiceType) {
      toast.error("Please specify the service type");
      setIsProcessing(false);
      return;
    }

    if (!formData.description) {
      toast.error("Please enter a description");
      setIsProcessing(false);
      return;
    }

    if (!formData.preferredDate) {
      toast.error("Please select a preferred date");
      setIsProcessing(false);
      return;
    }

    if (!formData.preferredTime) {
      toast.error("Please select a preferred time");
      setIsProcessing(false);
      return;
    }

    const finalServiceType =
      serviceType === "Others" ? otherServiceType : serviceType;

    const serviceData = {
      id: requestId,
      userId: currentUser.uid,
      userEmail: currentUser.email,
      userName: currentUser.displayName,
      identifier: formData.identifier,
      serviceType: finalServiceType,
      description: formData.description,
      preferredDate: formData.preferredDate,
      preferredTime: formData.preferredTime,
      image: formData.image || "",
      status: "Pending",
      createdAt: new Date().toISOString(),
    };

    try {
      await setDocData("serviceRequests", requestId, serviceData);
      toast.success("Service request submitted successfully");

      // Reset form and hide it
      setServiceType("");
      setOtherServiceType("");
      setFormData({
        identifier: currentUser.linkedIdentifiers[0],
        description: "",
        preferredDate: "",
        preferredTime: "",
      });
      setFiles({});
      setRequestId(v4().slice(0, 8).toUpperCase());
      setShowForm(false);
    } catch (error) {
      console.error("Error submitting request:", error);
      toast.error("Failed to submit service request");
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "#FFA500"; // Orange
      case "Scheduled":
        return "#4299e1"; // Blue
      case "In Progress":
        return "#9f7aea"; // Purple
      case "Completed":
        return "#8FCD00"; // Green
      case "Cancelled":
        return "#ef4444"; // Red
      default:
        return "#808080"; // Gray
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Pending":
        return <FaClock />;
      case "Scheduled":
        return <FaCalendarAlt />;
      case "In Progress":
        return <FaWrench />;
      case "Completed":
        return <FaCheck />;
      case "Cancelled":
        return <FaTimes />;
      default:
        return <FaClock />;
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedRequest(null);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    setIsAddingComment(true);
    try {
      // Create new comment object
      const commentObject = {
        message: commentText,
        createdBy: currentUser.displayName,
        createdAt: new Date().toLocaleString(),
        email: currentUser.email,
        id: Date.now().toString(),
      };

      // Add comment to existing comments array or create a new array
      const updatedComments = [
        ...(selectedRequest.comments || []),
        commentObject,
      ];

      // Update the request with new comment
      const updatedRequest = {
        ...selectedRequest,
        comments: updatedComments,
        updatedAt: new Date().toISOString(),
        history: [
          ...(selectedRequest?.history || []),
          {
            action: "Comment added",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to:
              commentText.substring(0, 30) +
              (commentText.length > 30 ? "..." : ""),
          },
        ],
      };

      // Update in Firebase
      await setDocData("serviceRequests", selectedRequest.id, updatedRequest);

      // Update local state
      setSelectedRequest(updatedRequest);
      setCommentText("");
      toast.success("Comment added successfully");
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setIsAddingComment(false);
    }
  };

  const renderRequestDetails = () => {
    if (!selectedRequest) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-[#363062] rounded-lg">
                <FaTools className="text-white text-sm" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#363062]">
                  Service Request Details
                </h3>
                <p className="text-xs text-gray-500">
                  Ref: {selectedRequest.id}
                </p>
              </div>
            </div>
            <button
              onClick={handleCloseDetails}
              className="text-gray-500 hover:text-gray-700"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>

          {/* Status Badge */}
          <div className="p-4 border-b border-gray-200">
            <div
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium gap-1.5"
              style={{
                backgroundColor: `${getStatusColor(selectedRequest.status)}15`,
                color: getStatusColor(selectedRequest.status),
              }}
            >
              {getStatusIcon(selectedRequest.status)}
              <span>{selectedRequest.status}</span>
            </div>
          </div>

          {/* Content Grid */}
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">Unit</div>
              <div className="text-sm font-medium text-[#363062]">
                {selectedRequest.identifier}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">
                Service Type
              </div>
              <div className="text-sm">{selectedRequest.serviceType}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">
                Date Submitted
              </div>
              <div className="text-sm">
                {formatDate(selectedRequest.createdAt)}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">
                Preferred Date & Time
              </div>
              <div className="text-sm">
                {formatDate(selectedRequest.preferredDate)} |{" "}
                {selectedRequest.preferredTime}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="p-4 border-t border-gray-100">
            <div className="text-xs font-medium text-gray-500 mb-2">
              Description
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-xs">
              {selectedRequest.description}
            </div>
          </div>

          {/* Image (if available) */}
          {selectedRequest.image && (
            <div className="p-4 border-t border-gray-100">
              <div className="text-xs font-medium text-gray-500 mb-2">
                Attached Image
              </div>
              <div className="rounded-lg overflow-hidden border border-gray-100">
                <PhotoCarousel images={[selectedRequest.image]} />
              </div>
            </div>
          )}

          {/* Assigned To */}
          <div className="p-4 border-t border-gray-100">
            <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1.5">
              <FaUsers className="text-gray-400" />
              <span>Assigned Staff</span>
            </div>
            {selectedRequest.assignedTo &&
            selectedRequest.assignedTo.length > 0 ? (
              <div className="space-y-2">
                {selectedRequest.assignedTo.map((staff, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                  >
                    <div className="w-6 h-6 bg-[#363062] rounded-full flex items-center justify-center text-white text-xs">
                      {staff.label?.charAt(0) || "U"}
                    </div>
                    <div className="text-xs font-medium">{staff.label}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-gray-500 italic">
                Not yet assigned
              </div>
            )}
          </div>

          {/* Estimated Cost */}
          <div className="p-4 border-t border-gray-100">
            <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1.5">
              <FaMoneyBillWave className="text-gray-400" />
              <span>Estimated Cost</span>
            </div>
            {selectedRequest.estimatedCost ? (
              <div className="text-sm font-bold text-[#363062]">
                ₱{selectedRequest.estimatedCost}
              </div>
            ) : (
              <div className="text-xs text-gray-500 italic">
                Not yet estimated
              </div>
            )}
          </div>

          {/* Resolution */}
          <div className="p-4 border-t border-gray-100">
            <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1.5">
              <FaCheckCircle className="text-gray-400" />
              <span>Resolution</span>
            </div>
            {selectedRequest.resolution ? (
              <div className="p-3 bg-gray-50 rounded-lg text-xs">
                {selectedRequest.resolution}
              </div>
            ) : (
              <div className="text-xs text-gray-500 italic">
                No resolution provided yet
              </div>
            )}
          </div>

          {/* Comments */}
          <div className="p-4 border-t border-gray-100">
            <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1.5">
              <FaComments className="text-gray-400" />
              <span>Comments</span>
            </div>

            {/* Comment Form */}
            <div className="mb-3">
              <form onSubmit={handleAddComment} className="space-y-2">
                <textarea
                  className="w-full rounded-md bg-gray-50 border border-gray-200 p-2.5 text-xs focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
                  placeholder="Add a comment..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  rows={2}
                  required
                />
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isAddingComment}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 ${
                      isAddingComment
                        ? "bg-gray-300 text-gray-600"
                        : "bg-[#363062] text-white hover:bg-[#2d2850]"
                    }`}
                  >
                    {isAddingComment ? (
                      <>
                        <div className="w-3 h-3 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <FaPaperPlane className="w-2.5 h-2.5" />
                        <span>Send Comment</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Comments List */}
            {selectedRequest.comments && selectedRequest.comments.length > 0 ? (
              <div className="space-y-3">
                {selectedRequest.comments.map((comment, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 bg-[#363062] rounded-full flex items-center justify-center text-white text-xs flex-shrink-0 mt-0.5">
                        {comment.createdBy?.charAt(0) || "U"}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div className="text-xs font-medium">
                            {comment.createdBy}
                          </div>
                          <div className="text-xs text-gray-500">
                            {comment.createdAt}
                          </div>
                        </div>
                        <div className="text-xs mt-1">{comment.message}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-gray-500 italic">
                No comments yet
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={handleCloseDetails}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-xs font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderRequestForm = () => {
    return (
      <div className="w-full mx-auto px-3">
        <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FaTools className="text-white text-base" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                Request Service
              </h1>
              <p className="text-xs text-gray-500">
                Submit a service request to property management
              </p>
              <p className="text-xs text-gray-500 mt-0.5">Ref: {requestId}</p>
            </div>
          </div>
          <button
            onClick={() => setShowForm(false)}
            className="flex items-center p-2 text-xs font-medium text-[#363062] rounded-full hover:bg-gray-100 transition-colors"
          >
            <FaChevronLeft className="w-3.5 h-3.5 mr-1" />
            Back
          </button>
        </div>

        <div className="space-y-4 mt-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
              <FaMapMarkerAlt className="text-xs" />
              {unitNumberLabel}
            </label>
            <Select
              options={identifiers}
              name="identifier"
              placeholder={`Select ${unitNumberLabel}`}
              onChange={handleSelectUnit}
              defaultValue={identifiers[0]}
              value={
                identifiers.find(
                  (option) => option.value === formData.identifier
                ) || identifiers[0]
              }
              styles={selectStyles}
              className="text-xs"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
              <FaWrench className="text-xs" />
              Service Type
            </label>
            <Select
              options={options}
              placeholder="Select Service Type"
              onChange={handleSelectServiceType}
              value={
                options.find((option) => option.value === serviceType) || null
              }
              styles={selectStyles}
              className="text-xs"
            />
          </div>

          {serviceType === "Others" && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
                <FaInfoCircle className="text-xs" />
                Specify Service Type
              </label>
              <input
                type="text"
                className="w-full rounded-md bg-gray-50 border border-gray-200 p-2.5 text-xs focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
                placeholder="Please specify the service type required"
                value={otherServiceType}
                onChange={(e) => setOtherServiceType(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
              <FaInfoCircle className="text-xs" />
              Description
            </label>
            <textarea
              className="w-full rounded-md bg-gray-50 border border-gray-200 p-2.5 text-xs focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
              rows={4}
              placeholder="Please provide details about the service needed..."
              name="description"
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
                <FaCalendarAlt className="text-xs" />
                Preferred Date
              </label>
              <input
                type="date"
                className="w-full rounded-md bg-gray-50 border border-gray-200 p-2.5 text-xs focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
                <FaClock className="text-xs" />
                Preferred Time
              </label>
              <Select
                options={timeSlots}
                placeholder="Select Preferred Time"
                onChange={handleSelectTime}
                value={
                  timeSlots.find(
                    (option) => option.value === formData.preferredTime
                  ) || null
                }
                styles={selectStyles}
                className="text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-md p-3">
              <FileUploadAndView
                doc="image"
                files={files}
                handleUpload={handleUpload}
                progress={progress}
                activeUpload={activeUpload}
              />
            </div>

            <div>
              {files.image && (
                <div className="space-y-2">
                  <div className="font-medium text-xs text-[#363062]">
                    Image Preview
                  </div>
                  <div className="relative h-64 w-full overflow-hidden rounded-lg border border-gray-200">
                    <img
                      src={files.image}
                      className="h-full w-full object-contain"
                      alt="Uploaded image"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 mb-4">
          <div className="max-w-md mx-auto">
            <button
              onClick={handleSubmit}
              disabled={isProcessing || activeUpload}
              className={`w-full py-3 rounded-md text-xs font-medium shadow-lg flex items-center justify-center gap-2 transition-colors ${
                isProcessing || activeUpload
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#363062] hover:bg-[#2d2850] text-white"
              }`}
            >
              {isProcessing ? (
                <>
                  <svg
                    className="animate-spin h-3.5 w-3.5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : activeUpload ? (
                <>
                  <svg
                    className="animate-spin h-3.5 w-3.5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Uploading Files... Please Wait
                </>
              ) : (
                <>
                  <FaPaperPlane className="text-xs" />
                  Submit Request
                </>
              )}
            </button>
            {activeUpload && (
              <p className="text-xs text-gray-500 text-center mt-2">
                Please wait while image is being uploaded (
                {Math.round(progress)}%)
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const filters = [
    "All",
    "Pending",
    "Scheduled",
    "In Progress",
    "Completed",
    "Cancelled",
  ];

  const renderRequestList = () => {
    return (
      <div className="w-full mx-auto px-4 max-w-4xl">
        <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FaTools className="text-white text-base" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                Service Requests
              </h1>
              <p className="text-xs text-gray-500">
                Manage your maintenance and service requests
              </p>
            </div>
          </div>
          <button
            className="flex items-center px-3 py-2 text-xs font-medium text-white rounded-md bg-[#8FCD00] shadow-sm hover:bg-[#7db700] transition-colors"
            onClick={() => setShowForm(true)}
          >
            <FaPlus className="w-3 h-3 mr-1.5" />
            New Request
          </button>
        </div>

        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FaSearch className="w-3.5 h-3.5 text-gray-500" />
          </div>
          <input
            type="text"
            className="block w-full p-2.5 pl-10 text-xs border rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
            placeholder="Search by service type, description, or unit..."
            value={searchQuery}
            onChange={handleSearch}
            style={{ borderColor: "#E2E8F0" }}
          />
          {searchQuery && (
            <button
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setSearchQuery("")}
            >
              <FaTimes className="w-3.5 h-3.5 text-gray-500" />
            </button>
          )}
        </div>

        <div className="mb-6 -mx-4 relative">
          <div className="overflow-x-auto px-4 pr-20 flex no-scrollbar relative">
            <div className="flex space-x-2">
              {filters.map((filter, index) => (
                <button
                  key={index}
                  className={`flex items-center whitespace-nowrap px-4 py-2 rounded-full text-xs font-medium transition-all ${
                    activeFilter === filter
                      ? "bg-[#363062] text-white shadow-sm"
                      : "bg-gray-50 text-[#363062] hover:bg-[#363062]/5"
                  }`}
                  onClick={() => setActiveFilter(filter)}
                >
                  {filter === "Pending" && (
                    <FaClock className="mr-1.5 text-xs" />
                  )}
                  {filter === "Scheduled" && (
                    <FaCalendarAlt className="mr-1.5 text-xs" />
                  )}
                  {filter === "In Progress" && (
                    <FaWrench className="mr-1.5 text-xs" />
                  )}
                  {filter === "Completed" && (
                    <FaCheck className="mr-1.5 text-xs" />
                  )}
                  {filter === "Cancelled" && (
                    <FaTimes className="mr-1.5 text-xs" />
                  )}
                  {filter === "All" && <FaFilter className="mr-1.5 text-xs" />}
                  <span>{filter}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="absolute right-0 top-0 h-full w-20 bg-gradient-to-l from-gray-100 via-gray-100 to-transparent pointer-events-none"></div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={toggleSortOrder}
            className="px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap border border-gray-200 bg-white text-[#363062] hover:bg-gray-50"
            title={sortOrder === "newest" ? "Newest first" : "Oldest first"}
          >
            {sortOrder === "newest" ? (
              <div className="flex items-center gap-1">
                <FaSortAmountDown className="w-3 h-3" />
                <span>Newest</span>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <FaSortAmountUp className="w-3 h-3" />
                <span>Oldest</span>
              </div>
            )}
          </button>
        </div>

        <div className="mt-4 space-y-3 pb-20">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#363062]"></div>
                <p className="mt-2 text-sm text-[#363062]">
                  Loading requests...
                </p>
              </div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
              <div className="p-3 bg-[#363062]/10 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <FaTools className="text-[#363062] text-lg" />
              </div>
              <h3 className="text-sm font-medium text-[#363062] mb-1">
                {userRequests.length === 0
                  ? "No Service Requests"
                  : "No Matching Results"}
              </h3>
              <p className="text-xs text-gray-500">
                {userRequests.length === 0
                  ? "You haven't submitted any service requests yet"
                  : activeFilter !== "All"
                  ? `No ${activeFilter} requests found`
                  : "Try adjusting your search terms"}
              </p>

              <button
                onClick={() => setShowForm(true)}
                className="mt-4 w-48 mx-auto bg-[#8FCD00] text-white py-2 rounded-lg flex items-center justify-center gap-2 text-xs font-medium"
              >
                <FaPlus className="w-3 h-3" />
                <span>Submit New Request</span>
              </button>
            </div>
          ) : (
            filteredRequests.map((request) => (
              <div
                key={request.id}
                className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md hover:translate-y-[-2px] cursor-pointer"
                onClick={() => handleViewDetails(request)}
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-[#363062]">
                      {request.identifier}
                    </div>
                    <div
                      className="flex items-center px-2 py-1 rounded-full text-xs font-medium gap-1.5"
                      style={{
                        backgroundColor: `${getStatusColor(request.status)}15`,
                        color: getStatusColor(request.status),
                      }}
                    >
                      <span className="text-xs">
                        {getStatusIcon(request.status)}
                      </span>
                      {request.status || "Unknown"}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                    <div className="flex items-center gap-1.5">
                      <FaWrench className="text-gray-400 flex-shrink-0" />
                      <span className="font-medium">{request.serviceType}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FaCalendarAlt className="text-gray-400 flex-shrink-0" />
                      <span>{formatDate(request.preferredDate)}</span>
                    </div>
                  </div>

                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <div className="text-xs text-gray-500 mb-1.5">
                      Description:
                    </div>
                    <div className="text-xs line-clamp-2">
                      {request.description}
                    </div>
                  </div>

                  {request.image && (
                    <div className="mt-3">
                      <div className="w-full h-24 rounded-lg overflow-hidden">
                        <img
                          src={request.image}
                          alt="Request"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center py-2 px-4 bg-gray-50 border-t border-gray-100">
                  <div className="text-xs text-gray-500">
                    {formatDate(request.createdAt)}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs font-medium text-[#363062]">
                    <FaClock className="text-xs" />
                    {request.preferredTime}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <UserScreenWindow label="Service Request">
      {showForm ? renderRequestForm() : renderRequestList()}
      {showDetails && renderRequestDetails()}
    </UserScreenWindow>
  );
}

export default ServiceRequest;
